import React, { useRef, useEffect } from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar, Line, Pie, Doughnut, PolarArea, Radar, Scatter, Bubble } from 'react-chartjs-2'
import 'chartjs-adapter-luxon'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { getCellColour } from 'components/spreadsheet/helpers/colouring'
import useProject from 'hooks/useProject'
import { ITableViewColumn, STACKED_CHART_TYPES, PROPORTIONAL_CHART_TYPES } from 'types'
import zoomPlugin from 'chartjs-plugin-zoom'
import Button from 'components/button'
import { openViewSettings } from 'components/spreadsheet/helpers/view-settings-bus'
import { Minus, Plus, Reset } from 'components/icons'
import annotationPlugin from 'chartjs-plugin-annotation'
import { contrastText } from 'helpers/colour'
import { getRowValue } from 'components/spreadsheet/helpers/functions'
import { formatPercentage } from 'components/spreadsheet/helpers/format'
import { useResolvedTheme, useThemedColours } from 'hooks/useTheme'
import { readTokenColour } from 'helpers/theme'
import ScaleToFit from 'components/spreadsheet/components/scale-to-fit'
import {
  MULTISELECT_COLUMN_KINDS,
  NUMERIC_COLUMN_KINDS,
  MAX_BAR_THICKNESS,
  OTHER_LABEL,
  isAggMode,
  getLabels,
  buildToday,
  buildSCurveChartData,
  orderLabels,
  colourForLabel,
  evaluateAggregatedValue,
  valueFor,
  buildLabelsByRow,
  buildAllLabelsFromLabelColumn,
  buildCardData,
  buildGroupedCardData,
  buildAggregatedPerSeries,
  buildFinalLabelsAndOther,
  buildStandardChartData,
  buildGanttChartData,
  buildLegend,
  formatColumnValue,
  type AggMode,
  type Agg
} from './helpers'

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  zoomPlugin,
  annotationPlugin
)

// Standard message for a configured chart with nothing to plot.
const NO_DATA_MESSAGE = 'No data to display in this chart'

const SpreadsheetChart: React.FC = () => {
  const themed = useThemedColours()
  // Re-read on every theme flip: canvas colours are resolved values, not tokens.
  const bodyColour = readTokenColour('--color-body', useResolvedTheme() === 'dark' ? '#e6e8eb' : '#333333')
  const { project } = useProject()
  const { spreadsheetData, showToolbar, fitToHeight } = useDataContext()

  const chartRef = useRef<ChartJS<'bar'> | null>(null)

  // When bitmap filtering is active, chart should only show visible rows
  const bitmap = spreadsheetData.visibleBitmap
  const chartRows = bitmap ? spreadsheetData.rows.filter((_, i) => bitmap[i]) : spreadsheetData.rows

  const today = buildToday()

  const defaultChartSettings = {
    chartType: null,
    showLegend: true,
    showValues: false,
    showPercentage: false,
    showTitle: false,
    showCardLabel: true,
    columnLabelId: '',
    columnValueIds: [],
    columnStackId: null,
    columnGanttStartDateId: null,
    columnGanttEndDateId: null,
    columnGanttDeadlineId: null,
    aggregate: null,
    sortAggregate: null,
    maxCategories: null
  }

  const chartSettings =
    spreadsheetData.userConfiguration.chartSettings === null
      ? spreadsheetData.viewDetails.chartSettings === null
        ? defaultChartSettings
        : spreadsheetData.viewDetails.chartSettings
      : spreadsheetData.userConfiguration.chartSettings

  const labelColumn =
    chartSettings && chartSettings.columnLabelId
      ? spreadsheetData.viewDetails.columns.find((column) => column.publicId === chartSettings.columnLabelId)
      : null
  const valueColumns =
    chartSettings && chartSettings.columnValueIds
      ? spreadsheetData.viewDetails.columns.filter((column) => chartSettings.columnValueIds.includes(column.publicId))
      : null
  const stackColumn =
    chartSettings && chartSettings.columnStackId
      ? spreadsheetData.viewDetails.columns.find((column) => column.publicId === chartSettings.columnStackId)
      : null

  const isNumericAggregation =
    labelColumn && valueColumns
      ? !MULTISELECT_COLUMN_KINDS.includes(labelColumn.kind) &&
        valueColumns.every((col) => NUMERIC_COLUMN_KINDS.includes(col.kind))
      : false
  const startColumn = chartSettings?.columnGanttStartDateId
    ? spreadsheetData.viewDetails.columns.find((c) => c.publicId === chartSettings.columnGanttStartDateId)
    : null

  const endColumn = chartSettings?.columnGanttEndDateId
    ? spreadsheetData.viewDetails.columns.find((c) => c.publicId === chartSettings.columnGanttEndDateId)
    : null

  const deadlineColumn = chartSettings?.columnGanttDeadlineId
    ? spreadsheetData.viewDetails.columns.find((c) => c.publicId === chartSettings.columnGanttDeadlineId)
    : null

  // ──────────────── S‑Curve helpers ────────────────
  const date1Column = chartSettings?.columnLabelId
    ? spreadsheetData.viewDetails.columns.find((c) => c.publicId === chartSettings.columnLabelId)
    : null

  const date2Column = chartSettings?.columnValueIds
    ? spreadsheetData.viewDetails.columns.find((c) => chartSettings.columnValueIds.includes(c.publicId))
    : null

  const sCurveChartData = buildSCurveChartData(
    chartSettings.chartType,
    date1Column,
    date2Column,
    chartRows,
    spreadsheetData.columnIdToIndex,
    spreadsheetData.columnStore
  )

  // small cache so label-based colours are stable within a render
  const colourCache = new Map<string, string>()

  const COUNT_DEFAULT: AggMode = 'count'
  const aggMode: AggMode = isAggMode(chartSettings.aggregate) ? chartSettings.aggregate : COUNT_DEFAULT

  const labelsByRow = buildLabelsByRow(
    labelColumn,
    chartRows,
    spreadsheetData.columnIdToIndex,
    spreadsheetData.columnStore
  )

  const allLabelsFromLabelColumn = buildAllLabelsFromLabelColumn(labelsByRow)

  // ──────────────── Card chart data ────────────────
  const cardData = buildCardData(
    chartSettings.chartType,
    valueColumns,
    chartRows,
    spreadsheetData.columnIdToIndex,
    spreadsheetData.columnStore,
    chartSettings.aggregate,
    aggMode
  )

  // ──────────────── Grouped card data (when label column is set) ────────────────
  const groupedCardData = buildGroupedCardData(
    chartSettings.chartType,
    labelColumn,
    valueColumns,
    chartRows,
    spreadsheetData.columnIdToIndex,
    spreadsheetData.columnStore,
    chartSettings.aggregate,
    aggMode,
    allLabelsFromLabelColumn,
    labelsByRow
  )

  // heavy aggregation
  const aggregatedPerSeries = buildAggregatedPerSeries(
    chartSettings.chartType,
    labelColumn,
    valueColumns,
    chartRows,
    spreadsheetData.columnIdToIndex,
    spreadsheetData.columnStore,
    aggMode,
    labelsByRow
  )

  // ――― 4. create the arrays Chart.js expects
  const firstCol = valueColumns && valueColumns[0]
  const firstSeries = firstCol ? aggregatedPerSeries[firstCol.publicId] : undefined

  // Always drive labels from the label column, not from aggregated values
  const baseForOrdering = Object.fromEntries(
    allLabelsFromLabelColumn.map((l) => [l, (firstSeries && firstSeries[l]) || { sum: 0, count: 0 }])
  )

  const aggregatedLabels = orderLabels(
    Object.keys(baseForOrdering),
    chartSettings.sortAggregate,
    spreadsheetData.userConfiguration.sortSettings,
    (lbl) => valueFor(baseForOrdering[lbl] || { sum: 0, count: 0 }, chartSettings.aggregate ?? 'count'),
    spreadsheetData.viewDetails.columns,
    chartSettings.columnLabelId ?? ''
  )

  // ──────────────── Top-N "Other" grouping ────────────────
  const { finalLabels, otherAggregated } = buildFinalLabelsAndOther(
    chartSettings.maxCategories,
    aggregatedLabels,
    firstSeries,
    aggMode,
    valueColumns,
    aggregatedPerSeries
  )

  const colourForLabelFn = (lbl: string) =>
    colourForLabel(
      lbl,
      colourCache,
      labelColumn!,
      spreadsheetData.columnValuesCount,
      spreadsheetData.userConfiguration.colourSettings,
      project.primaryColour
    )

  const standardChartData = buildStandardChartData(
    chartSettings.chartType,
    valueColumns,
    finalLabels,
    aggregatedPerSeries,
    otherAggregated,
    chartSettings.aggregate,
    isNumericAggregation,
    spreadsheetData.columnValuesCount,
    spreadsheetData.userConfiguration.colourSettings,
    project.primaryColour,
    colourForLabelFn
  )

  const ganttChartData = buildGanttChartData(
    chartSettings.chartType,
    labelColumn,
    startColumn,
    endColumn,
    deadlineColumn,
    chartRows,
    spreadsheetData.columnIdToIndex,
    spreadsheetData.columnStore,
    spreadsheetData.columnValuesCount,
    spreadsheetData.userConfiguration.colourSettings,
    project.primaryColour
  )

  // Custom plugin: draws a per-row deadline marker (vertical tick) on each Gantt bar
  const ganttDeadlinePlugin = {
    id: 'ganttDeadlineMarkers',
    afterDatasetsDraw(chart: any) {
      const dataset = chart.data.datasets?.[0]
      const deadlines: (number | null)[] = dataset?._deadlines
      if (!deadlines?.length) return

      const xScale = chart.scales.x
      const meta = chart.getDatasetMeta(0)
      if (!meta?.data?.length) return

      const ctx = chart.ctx
      ctx.save()

      deadlines.forEach((deadline: number | null, i: number) => {
        if (deadline == null) return
        const bar = meta.data[i]
        if (!bar) return

        const deadlinePixel = xScale.getPixelForValue(deadline)
        const barY = bar.y
        const barHeight = bar.height

        // Draw a vertical red line across the bar at the deadline position
        ctx.beginPath()
        ctx.strokeStyle = 'rgba(220, 38, 38, 0.95)'
        ctx.lineWidth = 2
        ctx.moveTo(deadlinePixel, barY - barHeight / 2 - 4)
        ctx.lineTo(deadlinePixel, barY + barHeight / 2 + 4)
        ctx.stroke()

        // Draw a small label above the bar
        ctx.font = '10px sans-serif'
        ctx.fillStyle = 'rgba(220, 38, 38, 0.95)'
        ctx.textAlign = 'center'
        ctx.fillText(deadlineColumn?.name || 'Deadline', deadlinePixel, barY - barHeight / 2 - 7)
      })

      ctx.restore()
    }
  }

  useEffect(() => {
    const canvas = chartRef.current?.canvas as HTMLCanvasElement | undefined
    if (!canvas || !ganttChartData) return

    const handler = (e: WheelEvent) => {
      if (!chartRef.current) return
      // deltaY is ≈120 per “notch” on a mouse wheel; invert for natural scroll
      chartRef.current.pan({ y: -e.deltaY, x: -e.deltaX }, undefined, 'none')
      e.preventDefault()
    }
    canvas.addEventListener('wheel', handler, { passive: false })
    return () => canvas.removeEventListener('wheel', handler)
  }, [])

  const agg: Agg = {}
  const labelsSet = new Set<string>()
  const stackColours: Record<string, string> = {}

  if (labelColumn && firstCol && stackColumn) {
    chartRows.forEach((row) => {
      const labels = getLabels(labelColumn, row, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
      const stackCat = getLabels(stackColumn, row, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)[0]
      const rawVal = getRowValue(row, firstCol.publicId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
      const { include, value } = evaluateAggregatedValue(rawVal, aggMode)
      // ensure every label from the label column shows on the axis
      labels.forEach((lbl) => {
        labelsSet.add(lbl) // ← moved up so the tick exists even when no value
      })

      if (!include) return
      const numeric = value

      // one colour per stack ...
      if (!stackColours[stackCat]) {
        const { backgroundColour } = getCellColour(
          stackColumn,
          spreadsheetData.columnValuesCount,
          row,
          stackCat,
          spreadsheetData.userConfiguration.colourSettings,
          false,
          project.primaryColour
        )
        stackColours[stackCat] = backgroundColour
      }
      labels.forEach((lbl) => {
        agg[stackCat] = agg[stackCat] || {}
        agg[stackCat][lbl] = agg[stackCat][lbl] || { sum: 0, count: 0 }
        agg[stackCat][lbl].sum += numeric
        agg[stackCat][lbl].count += 1
      })
    })
  }

  const totals: Record<string, number> = {}
  for (const byLabel of Object.values(agg)) {
    for (const [lbl, entry] of Object.entries(byLabel)) {
      totals[lbl] = (totals[lbl] || 0) + valueFor(entry, aggMode)
    }
  }

  const aggregatedLabelsStacked = orderLabels(
    Array.from(labelsSet),
    chartSettings.sortAggregate,
    spreadsheetData.userConfiguration.sortSettings,
    (lbl) => totals[lbl] ?? 0,
    spreadsheetData.viewDetails.columns,
    chartSettings.columnLabelId ?? ''
  )

  // Stacked/clustered charts aggregate the single value column (firstCol); mirror the
  // standard-chart percentage handling so decimal-stored percentage values render as %.
  const stackedPercentColumn = !!firstCol && firstCol.kind === 'percentage' && (aggMode === 'sum' || aggMode === 'avg')
  const stackedDecimalPlaces = firstCol?.decimalPlaces ?? 0

  const datasets = Object.entries(agg).map(([stackCat, byLabel]) => ({
    label: stackCat,
    data: aggregatedLabelsStacked.map((lbl) => (byLabel[lbl] ? valueFor(byLabel[lbl], aggMode) : 0)),
    backgroundColor: stackColours[stackCat] || project.primaryColour,
    stack: 'Stack 0',
    maxBarThickness: MAX_BAR_THICKNESS,
    _percentColumn: stackedPercentColumn,
    _decimalPlaces: stackedDecimalPlaces,
    _valueColumn: firstCol
  }))

  const clusteredDatasets = Object.entries(agg).map(([stackCat, byLabel], i) => ({
    label: stackCat,
    data: aggregatedLabelsStacked.map((lbl) => (byLabel[lbl] ? valueFor(byLabel[lbl], aggMode) : 0)),
    backgroundColor: stackColours[stackCat] || project.primaryColour,
    stack: `Stack ${i}`,
    maxBarThickness: MAX_BAR_THICKNESS,
    _percentColumn: stackedPercentColumn,
    _decimalPlaces: stackedDecimalPlaces,
    _valueColumn: firstCol
  }))

  const stackedChartData = { labels: aggregatedLabelsStacked, datasets }
  const clusteredChartData = { labels: aggregatedLabelsStacked, datasets: clusteredDatasets }

  // Stamp toggle state onto every dataset so that the datalabels
  // display/formatter callbacks can read it via ctx.dataset instead of
  // closing over React state.  react-chartjs-2's cloneData() only copies
  // labels + datasets (dropping root-level custom props), but it spreads
  // each dataset object, so per-dataset flags survive into chart.data.
  const _showValues = chartSettings?.showValues ?? false
  const _showPercentage = chartSettings?.showPercentage ?? false
  for (const d of [standardChartData, stackedChartData, clusteredChartData]) {
    for (const ds of d.datasets as any[]) {
      ds._showValues = _showValues
      ds._showPercentage = _showPercentage
    }
  }

  const isStackedType = STACKED_CHART_TYPES.includes(chartSettings.chartType!)

  const dataLabelPos = isStackedType
    ? { anchor: 'center' as const, align: 'center' as const, offset: 0, clamp: true }
    : { anchor: 'end' as const, align: 'end' as const, offset: 4, clamp: false }

  // Percentage columns store decimals (0.62 = 62%). The data labels already format these;
  // do the same for tooltips (per-dataset, via ctx.dataset._percentColumn) and value-axis
  // ticks (component-level, since tick callbacks have no dataset context) so hover values
  // and axis labels match the spreadsheet cell + data-label formatting (#10266).
  const aggIsValueBased = aggMode === 'sum' || aggMode === 'avg'
  // Date aggregates are card-only (the chart menu resets them when the type changes), but a
  // view saved before that reset can still carry one. `valueFor` plots a row count in that
  // case, so the data label must format it as a count rather than as a date.
  const labelAggregate = aggMode === 'earliest' || aggMode === 'latest' ? 'count' : aggMode
  // Tooltip is per-dataset, so format whenever any value column is a percentage.
  const anyPercentValueColumn =
    !!valueColumns && valueColumns.some((col) => col.kind === 'percentage') && aggIsValueBased
  // The value axis is shared, so only format its ticks when every value column is a percentage.
  const percentValueAxis =
    !!valueColumns &&
    valueColumns.length > 0 &&
    valueColumns.every((col) => col.kind === 'percentage') &&
    aggIsValueBased
  const percentValueDecimals = valueColumns && valueColumns[0] ? (valueColumns[0].decimalPlaces ?? 0) : 0

  const percentTooltipLabel = (ctx: any): string => {
    const prefix = ctx.dataset?.label ? `${ctx.dataset.label}: ` : ''
    if (ctx.dataset?._percentColumn) {
      return `${prefix}${formatPercentage(Number(ctx.raw) * 100, ctx.dataset._decimalPlaces ?? 0)}`
    }
    // Reconstruct Chart.js's default label so non-percentage datasets are unaffected.
    return `${prefix}${ctx.formattedValue}`
  }
  const percentTooltip = anyPercentValueColumn ? { callbacks: { label: percentTooltipLabel } } : {}

  // Merge a percentage tick callback onto the given value axes, preserving existing axis config.
  const withPercentValueAxis = (options: any, axes: Array<'x' | 'y' | 'y1' | 'r'>): any => {
    if (!percentValueAxis) return options
    const tickCallback = (value: number | string): string => formatPercentage(Number(value) * 100, percentValueDecimals)
    const scales = { ...(options.scales ?? {}) }
    for (const axis of axes) {
      const existingAxis = scales[axis] ?? {}
      scales[axis] = { ...existingAxis, ticks: { ...(existingAxis.ticks ?? {}), callback: tickCallback } }
    }
    return { ...options, scales }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    animation: false,
    layout: { padding: { top: 20, bottom: 20, left: 10, right: 50 } },
    plugins: {
      title: {
        display: chartSettings.showTitle,
        text: `${spreadsheetData.viewDetails.name}`,
        color: bodyColour,
        font: { size: 18 },
        padding: { top: 10, bottom: 30 }
      },
      legend: buildLegend(chartSettings.chartType!, chartSettings.showLegend ?? true, project.primaryColour),
      tooltip: { usePointStyle: true, ...percentTooltip },
      datalabels: {
        display: (ctx: any) => {
          // Read from ctx.dataset (not React closures) so the Word export
          // OffscreenCanvas clone sees the current toggle state.
          if (!ctx.dataset._showValues && !ctx.dataset._showPercentage) return false
          const v = Array.isArray(ctx.dataset.data) ? ctx.dataset.data[ctx.dataIndex] : ctx.raw
          return v !== null && Number(v) !== 0
        },
        formatter: (v: any, ctx: any) => {
          if (v === null || Number(v) === 0) return ''
          if (ctx.dataset._showPercentage) {
            const dataset = ctx.dataset.data as number[]
            const total = dataset.reduce((sum: number, val: number | null) => sum + (val === null ? 0 : Number(val)), 0)
            if (total === 0) return ''
            const pct = (Number(v) / total) * 100
            return `${pct.toFixed(1)}%`
          }
          // Format through the value column so the label matches the grid cell: decimal
          // places, thousand separator, percentage columns stored as decimals (0.62 = 62%),
          // durations as hh:mm (#10734). Falls back for datasets with no column stamped.
          const valueColumn = ctx.dataset._valueColumn
          if (!valueColumn) return Number(v).toLocaleString('en-US')
          return formatColumnValue(Number(v), valueColumn, labelAggregate)
        },
        color: (ctx: any) => {
          if (isStackedType) {
            const bg = ctx.dataset.backgroundColor
            const hex = Array.isArray(bg) ? (bg[ctx.dataIndex] as string) : (bg as string)
            return contrastText(hex ?? project.primaryColour)
          }
          return bodyColour
        },
        ...dataLabelPos,
        font: { size: 12 }
      }
    }
  }

  // Only offer the settings shortcut when the toolbar is available (hidden in documents).
  const MissingConfigMessage: React.FC<{ message: string }> = ({ message }) => (
    <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-8 text-center bg-surface">
      <div className="text-lg font-medium text-body-soft">{message}</div>
      {showToolbar && (
        <Button internalType="primary" onClick={() => openViewSettings('table-chart-button')}>
          Open chart settings
        </Button>
      )}
    </div>
  )

  const renderChart = () => {
    const chartType = chartSettings.chartType || ''

    const stackedOrClustered = [
      'stacked-bar',
      'stacked-horizontal-bar',
      'clustered-bar',
      'clustered-horizontal-bar'
    ].includes(chartType)

    // ───────────────────────
    // 1. Stacked / clustered bar requirements
    // ───────────────────────
    if (stackedOrClustered) {
      if (!labelColumn && (!valueColumns || valueColumns.length === 0) && !stackColumn) {
        return (
          <MissingConfigMessage message="To show this stacked/clustered chart, please select a label column, a stack column, and a value column." />
        )
      }

      if (!labelColumn) {
        return <MissingConfigMessage message="Please select a label column for this stacked/clustered chart." />
      }

      if (!stackColumn) {
        return <MissingConfigMessage message="Please select a stack column for this stacked/clustered chart." />
      }

      if (!valueColumns || valueColumns.length === 0) {
        return <MissingConfigMessage message="Please select a value column for this stacked/clustered chart." />
      }

      if (valueColumns.length > 1) {
        // your existing message, kept as-is
        return (
          <div className="flex items-center justify-center text-muted h-full text-center px-6">
            Stacked/clustered charts currently support one value column. Please select a single value column.
          </div>
        )
      }

      // Configured, but no categories to plot (empty view / filtered out).
      if (aggregatedLabelsStacked.length === 0) {
        return <MissingConfigMessage message={NO_DATA_MESSAGE} />
      }
    }

    // ───────────────────────
    // 2. “Standard” charts that need label + value columns
    // ───────────────────────
    const needsLabelAndValue = [
      'bar',
      'horizontal-bar',
      'grouped-bar',
      'line',
      'area',
      'multiaxis-line',
      'pie',
      'doughnut',
      'polar-area',
      'radar',
      'scatter',
      'bubble'
    ]

    if (needsLabelAndValue.includes(chartType)) {
      if (!labelColumn && (!valueColumns || valueColumns.length === 0)) {
        return (
          <MissingConfigMessage message="To show this chart, please select a label column and at least one value column." />
        )
      }

      if (!labelColumn) {
        return <MissingConfigMessage message="Please select a label column for this chart." />
      }

      if (!valueColumns || valueColumns.length === 0) {
        return <MissingConfigMessage message="Please select at least one value column for this chart." />
      }

      // Configured, but no categories (empty view), or a proportional chart with only
      // zero values — both otherwise render a blank canvas.
      const hasNonZeroValue = standardChartData.datasets.some(
        (ds) => Array.isArray(ds.data) && (ds.data as (number | null)[]).some((v) => v !== null && Number(v) !== 0)
      )
      if (finalLabels.length === 0 || (PROPORTIONAL_CHART_TYPES.includes(chartType) && !hasNonZeroValue)) {
        return <MissingConfigMessage message={NO_DATA_MESSAGE} />
      }
    }

    // ───────────────────────
    // 3. S-curve & Gantt already have their own guards
    // ───────────────────────
    switch (chartSettings.chartType) {
      case 'bar':
        return (
          <Bar
            key="bar"
            options={withPercentValueAxis(chartOptions, ['y']) as any}
            data={standardChartData}
            plugins={[ChartDataLabels]}
          />
        )
      case 'horizontal-bar':
        return (
          <Bar
            key="horizontal-bar"
            options={withPercentValueAxis({ ...chartOptions, indexAxis: 'y' }, ['x']) as any}
            data={standardChartData}
            plugins={[ChartDataLabels]}
          />
        )
      case 'stacked-bar':
        return (
          <Bar
            key="stacked-bar"
            options={
              withPercentValueAxis(
                {
                  ...chartOptions,
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true }
                  }
                },
                ['y']
              ) as any
            }
            data={stackedChartData}
            plugins={[ChartDataLabels]}
          />
        )
      case 'stacked-horizontal-bar':
        return (
          <Bar
            key="stacked-horizontal-bar"
            options={
              withPercentValueAxis(
                {
                  ...chartOptions,
                  indexAxis: 'y',
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true }
                  }
                },
                ['x']
              ) as any
            }
            data={stackedChartData}
            plugins={[ChartDataLabels]}
          />
        )
      case 'clustered-bar':
        return (
          <Bar
            key="clustered-bar"
            data={clusteredChartData}
            options={
              withPercentValueAxis(
                {
                  ...chartOptions,
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true }
                  }
                },
                ['y']
              ) as any
            }
            plugins={[ChartDataLabels]}
          />
        )

      case 'clustered-horizontal-bar':
        return (
          <Bar
            key="clustered-horizontal-bar"
            data={clusteredChartData}
            options={
              withPercentValueAxis(
                {
                  ...chartOptions,
                  indexAxis: 'y',
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true }
                  }
                },
                ['x']
              ) as any
            }
            plugins={[ChartDataLabels]}
          />
        )
      case 'grouped-bar':
        return (
          <Bar
            key="grouped-bar"
            options={withPercentValueAxis(chartOptions, ['y']) as any}
            data={standardChartData}
            plugins={[ChartDataLabels]}
          />
        )
      case 'line':
        return (
          <Line
            key="line"
            options={
              withPercentValueAxis(
                {
                  ...chartOptions,
                  scales: {
                    x: {
                      ticks: {
                        callback: (val: any, idx: number) => {
                          const lbl = (standardChartData.labels as string[])[idx]
                          return lbl === '' ? '—' : lbl
                        }
                      }
                    }
                  },
                  plugins: {
                    ...chartOptions.plugins,
                    tooltip: {
                      usePointStyle: true,
                      filter: (ctx: any) => ctx.raw !== null,
                      ...percentTooltip
                    }
                  },
                  elements: { line: { spanGaps: false } }
                },
                ['y']
              ) as any
            }
            data={{
              ...standardChartData,
              datasets: standardChartData.datasets.map((ds) => ({
                ...ds,
                pointRadius: (ctx: any) => (ctx.raw === null ? 0 : 2),
                spanGaps: false
              }))
            }}
            plugins={[ChartDataLabels]}
          />
        )
      case 'area':
        return (
          <Line
            key="area"
            options={
              withPercentValueAxis(
                {
                  ...chartOptions,
                  scales: {
                    x: {
                      ticks: {
                        callback: (val: any, idx: number) => {
                          const lbl = (standardChartData.labels as string[])[idx]
                          return lbl === '' ? '—' : lbl
                        }
                      }
                    }
                  },
                  plugins: {
                    ...chartOptions.plugins,
                    tooltip: {
                      usePointStyle: true,
                      filter: (ctx: any) => ctx.raw !== null,
                      ...percentTooltip
                    }
                  },
                  elements: { line: { spanGaps: false } }
                },
                ['y']
              ) as any
            }
            data={{
              ...standardChartData,
              datasets: standardChartData.datasets.map((ds) => ({
                ...ds,
                fill: true,
                pointRadius: (ctx: any) => (ctx.raw === null ? 0 : 2),
                spanGaps: false
              }))
            }}
            plugins={[ChartDataLabels]}
          />
        )
      case 'multiaxis-line':
        return (
          <Line
            key="multiaxis-line"
            options={
              withPercentValueAxis(
                {
                  ...chartOptions,
                  elements: { line: { spanGaps: false } },
                  scales: {
                    y: { type: 'linear', position: 'left' },
                    y1: { type: 'linear', position: 'right', grid: { drawOnChartArea: false } }
                  }
                },
                ['y', 'y1']
              ) as any
            }
            data={{
              ...standardChartData,
              datasets: standardChartData.datasets.map((ds, i) => ({
                ...ds,
                yAxisID: i === 0 ? 'y' : 'y1',
                pointRadius: (ctx: any) => (ctx.raw === null ? 0 : 2),
                spanGaps: false
              }))
            }}
            plugins={[ChartDataLabels]}
          />
        )
      case 'pie':
        return (
          <Pie
            key="pie"
            options={chartOptions as any}
            data={{
              ...standardChartData,
              datasets: standardChartData.datasets.map((ds) => ({
                ...ds,
                backgroundColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                borderColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                hoverBorderColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                borderWidth: 1,
                hoverBorderWidth: 1,
                borderAlign: 'inner'
              }))
            }}
            plugins={[ChartDataLabels]}
          />
        )
      case 'doughnut':
        return (
          <Doughnut
            key="doughnut"
            options={chartOptions as any}
            data={{
              ...standardChartData,
              datasets: standardChartData.datasets.map((ds) => ({
                ...ds,
                backgroundColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                borderColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                hoverBorderColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                borderWidth: 1,
                hoverBorderWidth: 1,
                borderAlign: 'inner'
              }))
            }}
            plugins={[ChartDataLabels]}
          />
        )
      case 'polar-area':
        return (
          <PolarArea
            key="polar-area"
            options={chartOptions as any}
            data={{
              ...standardChartData,
              datasets: standardChartData.datasets.map((ds) => ({
                ...ds,
                backgroundColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                borderColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                hoverBorderColor: finalLabels.map((lbl) => (lbl === OTHER_LABEL ? '#9E9E9E' : colourForLabelFn(lbl))),
                borderWidth: 1,
                hoverBorderWidth: 1,
                borderAlign: 'inner'
              }))
            }}
            plugins={[ChartDataLabels]}
          />
        )
      case 'radar':
        return (
          <Radar
            key="radar"
            options={withPercentValueAxis(chartOptions, ['r']) as any}
            data={standardChartData}
            plugins={[ChartDataLabels]}
          />
        )
      case 'scatter':
        return (
          <Scatter
            key="scatter"
            options={withPercentValueAxis(chartOptions, ['y']) as any}
            data={standardChartData}
            plugins={[ChartDataLabels]}
          />
        )
      case 'bubble': {
        const bubbleData =
          labelColumn && firstCol
            ? {
                datasets: [
                  {
                    label: firstCol ? firstCol.name : '',
                    data: chartRows.map((row) => ({
                      x:
                        Number(
                          getRowValue(
                            row,
                            labelColumn.publicId,
                            spreadsheetData.columnIdToIndex,
                            spreadsheetData.columnStore
                          )
                        ) || 0,
                      y:
                        Number(
                          getRowValue(
                            row,
                            firstCol.publicId,
                            spreadsheetData.columnIdToIndex,
                            spreadsheetData.columnStore
                          )
                        ) || 0,
                      r: 10
                    })),
                    backgroundColor: 'rgba(255, 99, 132, 0.5)'
                  }
                ]
              }
            : {
                datasets: []
              }
        return <Bubble key="bubble" options={chartOptions as any} data={bubbleData} plugins={[ChartDataLabels]} />
      }
      case 's-curve':
        if (sCurveChartData) {
          return <Bar key="s-curve" options={chartOptions as any} data={sCurveChartData} plugins={[ChartDataLabels]} />
        }
        if (!date1Column) {
          return <MissingConfigMessage message="Select Date 1 (and optionally Date 2) columns." />
        }
        // No rows, or none with usable dates.
        return <MissingConfigMessage message={NO_DATA_MESSAGE} />

      case 'gantt':
        if (!ganttChartData) {
          return <MissingConfigMessage message="Select label, start and end columns for the Gantt chart." />
        }
        // Configured, but no rows have valid start/end dates.
        if (ganttChartData.datasets[0]?.data?.length === 0) {
          return <MissingConfigMessage message={NO_DATA_MESSAGE} />
        }
        return (
          <Bar
            ref={chartRef}
            key="gantt"
            data={{
              ...ganttChartData,
              datasets: ganttChartData.datasets.map((ds: any) => ({
                ...ds,
                backgroundColor: (ctx: any) => {
                  const i = ctx.dataIndex
                  const deadline = ds._deadlines?.[i]
                  const normalColor = ds._normalColours?.[i] || project.primaryColour

                  if (deadline == null || !ctx.raw) return normalColor

                  const raw = ctx.raw as any
                  if (!raw?.x) return normalColor

                  const startTime = new Date(raw.x[0]).getTime()
                  const endTime = new Date(raw.x[1]).getTime()

                  if (endTime <= deadline) return normalColor
                  if (startTime >= deadline) return 'rgba(220, 38, 38, 0.85)'

                  const xScale = ctx.chart.scales.x
                  if (!xScale) return normalColor

                  const startPixel = xScale.getPixelForValue(startTime)
                  const endPixel = xScale.getPixelForValue(endTime)
                  if (endPixel <= startPixel) return normalColor

                  const deadlinePixel = xScale.getPixelForValue(deadline)
                  const ratio = Math.max(0, Math.min(1, (deadlinePixel - startPixel) / (endPixel - startPixel)))

                  const gradient = ctx.chart.ctx.createLinearGradient(startPixel, 0, endPixel, 0)
                  gradient.addColorStop(0, normalColor)
                  gradient.addColorStop(ratio, normalColor)
                  gradient.addColorStop(ratio, 'rgba(220, 38, 38, 0.85)')
                  gradient.addColorStop(1, 'rgba(220, 38, 38, 0.85)')

                  return gradient
                }
              }))
            }}
            options={{
              ...chartOptions,
              layout: {
                padding: { top: 20, bottom: 60, left: 10, right: 50 }
              },

              transitions: {
                zoom: {
                  animation: {
                    duration: 0
                  }
                }
              },
              indexAxis: 'y' as const,
              scales: {
                x: {
                  type: 'time' as const,
                  min: ganttChartData._minDate
                    ? ganttChartData._minDate.toISOString()
                    : new Date('2023-01-01').toISOString(),
                  max: ganttChartData._maxDate
                    ? ganttChartData._maxDate.toISOString()
                    : new Date('2025-12-31').toISOString(),
                  time: {
                    unit: 'day',
                    displayFormats: {
                      day: 'yyyy-MM-dd'
                    },
                    tooltipFormat: 'DD MMM yyyy'
                  },
                  adapters: {
                    date: {
                      locale: 'en' // Ensure the locale is set correctly
                    }
                  },
                  ticks: {
                    source: 'auto',
                    autoSkip: true,
                    maxRotation: 45,
                    display: true // Ensure ticks are displayed
                  },
                  grid: { display: true } // Show grid lines for better readability
                },
                y: {
                  type: 'category' as const,
                  offset: true
                }
              },
              animation: false,
              plugins: {
                ...chartOptions.plugins,
                annotation: {
                  annotations: {
                    todayLine: {
                      type: 'line',
                      xMin: today.getTime(),
                      xMax: today.getTime(),
                      borderColor: 'rgba(255,0,0,0.85)',
                      borderWidth: 2,
                      label: {
                        display: true,
                        content: 'Today',
                        position: 'start',
                        backgroundColor: 'color-mix(in srgb, var(--color-surface-raised) 85%, transparent)',
                        color: 'rgba(255,0,0,0.85)'
                      }
                    }
                  }
                },

                zoom: {
                  limits: {
                    x: {
                      min: ganttChartData._minDate
                        ? ganttChartData._minDate.getTime()
                        : new Date('2023-01-01').getTime(),
                      max: ganttChartData._maxDate
                        ? ganttChartData._maxDate.getTime()
                        : new Date('2025-12-31').getTime(),
                      minRange: 1000 * 60 * 60 * 24 * 7 // 1 week
                    }
                  },
                  zoom: {
                    wheel: { enabled: true, modifierKey: 'ctrl' }, // hold ⌃ / Ctrl to zoom
                    pinch: { enabled: true }, // hold ⌃ / Ctrl to zoom
                    drag: { enabled: true, modifierKey: 'shift' }, // hold ⌥ / Alt to zoom
                    mode: 'xy'
                  },
                  pan: {
                    enabled: true,
                    mode: 'xy',
                    // modifierKey: 'shift', // hold ⌃ / Ctrl to pan,
                    scaleMode: 'xy',
                    threshold: 8
                  }
                },
                tooltip: {
                  callbacks: {
                    // Custom tooltip to display date range properly
                    label: function (context) {
                      const dataPoint = context.raw as any
                      if (dataPoint && dataPoint.x) {
                        const start = new Date(dataPoint.x[0]).toLocaleDateString()
                        const end = new Date(dataPoint.x[1]).toLocaleDateString()

                        const durationDays = Math.round(
                          (new Date(dataPoint.x[1]).getTime() - new Date(dataPoint.x[0]).getTime()) / (1000 * 3600 * 24)
                        )
                        const durationText = durationDays > 0 ? ` (${durationDays} days)` : ''

                        return `${start} to ${end}${durationText}`
                      }
                      return ''
                    }
                  }
                },
                legend: { display: false }
              }
            }}
            plugins={[ChartDataLabels, ganttDeadlinePlugin]}
          />
        )
      case 'card': {
        if ((!cardData || cardData.length === 0) && !groupedCardData) {
          return <MissingConfigMessage message="Please select at least one value column for the card chart." />
        }
        // Grouped label matches no rows, or a flat card over an empty view.
        if ((groupedCardData && groupedCardData.length === 0) || (!groupedCardData && chartRows.length === 0)) {
          return <MissingConfigMessage message={NO_DATA_MESSAGE} />
        }
        const isDateAgg = aggMode === 'earliest' || aggMode === 'latest'
        const colourSettings =
          spreadsheetData.userConfiguration.colourSettings ?? spreadsheetData.viewDetails.colourSettings

        const renderCardTile = (
          card: { column: ITableViewColumn; value: number | string },
          total: number,
          label: string,
          key: string,
          colourOverride?: { backgroundColour: string; fontColour: string }
        ) => {
          const colour =
            colourOverride ??
            getCellColour(
              card.column,
              spreadsheetData.columnValuesCount,
              null,
              card.value,
              colourSettings,
              false,
              undefined,
              null
            )
          // A card with no matching colour rule comes back holding whichever
          // default getCellColour was given, which is a token rather than a
          // literal — comparing against '#fff' silently stopped matching when
          // that default changed, and every card then claimed to have a rule.
          const hasColourRule = !colour.backgroundColour.startsWith('var(')
          const cardBg = hasColourRule ? themed.fill(colour.backgroundColour) : 'var(--color-surface-raised)'
          const cardFont = hasColourRule ? themed.text(colour.fontColour, colour.backgroundColour) : 'var(--color-body)'
          return (
            <div
              key={key}
              data-card-tile
              className="flex flex-col items-center justify-center rounded-lg shadow-md p-[24px] min-w-[180px] max-w-[300px] flex-1"
              style={{ backgroundColor: cardBg, color: cardFont }}
            >
              {chartSettings.showValues && (
                <div className="text-4xl font-bold leading-tight">
                  {formatColumnValue(card.value, card.column, aggMode)}
                </div>
              )}
              {chartSettings.showCardLabel && (
                <div className="text-sm font-medium mt-[8px] opacity-80 text-center truncate w-full tracking-wide">
                  {label}
                </div>
              )}
              {chartSettings.showPercentage && !isDateAgg && total !== 0 && (
                <div
                  className={
                    !chartSettings.showValues ? 'text-4xl font-bold leading-tight' : 'text-sm mt-[4px] opacity-70'
                  }
                >
                  {(((card.value as number) / total) * 100).toFixed(1)}%
                </div>
              )}
            </div>
          )
        }

        // Cards are plain DOM, not Chart.js, so the view-name heading is rendered by hand.
        // It mirrors the Chart.js `plugins.title` config used by every other chart type:
        // 18px bold in --color-body, 30px of space above and below. The 30px below is
        // split as pb-[6px] here plus the card grid's own 24px top padding.
        const cardTitle = chartSettings.showTitle && (
          <div
            data-card-chart-title
            className="shrink-0 pt-[30px] pb-[6px] px-[24px] text-center text-[18px] font-bold text-body"
          >
            {spreadsheetData.viewDetails.name}
          </div>
        )
        const renderCardLayout = (cards: React.ReactNode) => {
          // Fit to height: lay the cards out at their natural height and shrink the lot (title included)
          // to the insert, rather than scrolling the card grid.
          if (fitToHeight) {
            return (
              <ScaleToFit>
                {cardTitle}
                <div className="flex flex-wrap gap-[16px] p-[24px] items-start justify-center">{cards}</div>
              </ScaleToFit>
            )
          }
          return (
            <div className="flex flex-col w-full h-full">
              {cardTitle}
              <div className="flex flex-wrap gap-[16px] p-[24px] items-start justify-center flex-1 min-h-0 overflow-y-auto [align-content:safe_center]">
                {cards}
              </div>
            </div>
          )
        }

        // Grouped mode: flat grid with combined "group value + column name" labels
        // Colour rules from the label column apply to each group's cards
        if (groupedCardData && groupedCardData.length > 0) {
          const allCards = groupedCardData.flatMap((group) => {
            // Evaluate colour for this group using the label column's colour rules
            const groupColour = labelColumn
              ? getCellColour(
                  labelColumn,
                  spreadsheetData.columnValuesCount,
                  null,
                  group.label,
                  colourSettings,
                  false,
                  undefined,
                  null
                )
              : undefined
            return group.cards.map((card) => ({
              ...card,
              label:
                (valueColumns?.length ?? 0) > 1
                  ? `${group.label || '(empty)'} ${card.column.name}`
                  : `${group.label || '(empty)'}`,
              key: `${group.label}-${card.column.publicId}`,
              groupColour
            }))
          })
          const total = isDateAgg ? 0 : allCards.reduce((s, c) => s + (c.value as number), 0)
          return renderCardLayout(
            allCards.map((card) => renderCardTile(card, total, card.label, card.key, card.groupColour))
          )
        }

        // Flat mode: one card per value column
        const total = isDateAgg ? 0 : cardData!.reduce((s, c) => s + (c.value as number), 0)
        return renderCardLayout(
          cardData!.map((card) => renderCardTile(card, total, card.column.name, card.column.publicId))
        )
      }
      default:
        return <MissingConfigMessage message="Select a chart type to get started" />
    }
  }

  return (
    <div
      className="bg-surface rounded shadow border-box w-full h-full bg-surface"
      data-table-id={spreadsheetData.tableDetails.publicId}
      data-view-id={spreadsheetData.viewDetails.publicId}
      data-chart-type={chartSettings.chartType || undefined}
    >
      {renderChart()}

      {chartSettings.chartType === 'gantt' && ganttChartData && (
        <div className="absolute flex items-center gap-2.5 bottom-[10px] right-[10px]">
          {/* helpers */}
          <div className="flex items-center justify-center h-full">
            <b>Move</b>: Mouse Click + Drag / Mouse Wheel
          </div>
          <div className="flex items-center justify-center h-full">
            <b>Zoom</b>: <kbd>Ctrl</kbd> + Mouse Wheel
          </div>
          <div className="flex items-center justify-center h-full">
            <b>Zoom Area</b>: <kbd>Shift</kbd> + Mouse Drag
          </div>
          {/* zoom- */}
          <Button
            internalType="grey"
            onClick={() => {
              const c = chartRef.current
              if (!c) return
              c.zoom(0.8, 'none') // factor > 1  → zoom *out*
            }}
            className="ml-auto"
          >
            <Minus />
            <span className="ml-[5px]">Zoom Out</span>
          </Button>

          <Button
            internalType="grey"
            onClick={() => {
              const c = chartRef.current
              if (!c) return
              c.zoom(1.2, 'none') // factor < 1  → zoom *in*
            }}
          >
            <Plus />
            <span className="ml-[5px]">Zoom In</span>
          </Button>

          <Button
            internalType="grey"
            onClick={() => {
              chartRef.current?.resetZoom('none')
            }}
          >
            <Reset />
            <span className="ml-[5px]">Reset Zoom</span>
          </Button>
        </div>
      )}
    </div>
  )
}

export default SpreadsheetChart
